import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch, getStoredUser, parseJsonSafe, setAuth } from '../api/client';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_SESSION_ACTIVITY_EVENT,
  AUTH_SESSION_WARNING_EVENT,
  clearAuth,
  getLogoutRedirectPath,
  recordSessionActivity,
} from '../auth/session';
import useModalFocusTrap from './useModalFocusTrap';

function SessionExpiryGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [mustReauthenticate, setMustReauthenticate] = useState(false);
  const [email, setEmail] = useState(() => getStoredUser()?.email || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const passwordRef = useRef(null);

  useModalFocusTrap({
    active: mustReauthenticate,
    containerRef: dialogRef,
    initialFocusRef: passwordRef,
  });

  useEffect(() => {
    const warn = () => setShowWarning(true);
    const expire = () => {
      setShowWarning(false);
      setMustReauthenticate(true);
    };
    const activity = () => setShowWarning(false);
    window.addEventListener(AUTH_SESSION_WARNING_EVENT, warn);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, expire);
    window.addEventListener(AUTH_SESSION_ACTIVITY_EVENT, activity);
    return () => {
      window.removeEventListener(AUTH_SESSION_WARNING_EVENT, warn);
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, expire);
      window.removeEventListener(AUTH_SESSION_ACTIVITY_EVENT, activity);
    };
  }, []);

  const goToSignInPage = useCallback(() => {
    const previousUserId = getStoredUser()?.id ?? null;
    const nextPath = `${location.pathname}${location.search}${location.hash}`;
    clearAuth();
    navigate(
      getLogoutRedirectPath({ reason: 'expired', previousUserId, nextPath }),
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!mustReauthenticate) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        goToSignInPage();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goToSignInPage, mustReauthenticate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const response = await apiFetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = (await parseJsonSafe(response)) || {};
      if (!response.ok) throw new Error(data?.detail || 'Sign in failed. Please try again.');
      const previousUser = getStoredUser();
      if (!data?.access_token || String(data?.user?.id) !== String(previousUser?.id)) {
        throw new Error('Sign in with the same account to keep this lesson open.');
      }
      setAuth(data.access_token, data.user);
      setPassword('');
      setMustReauthenticate(false);
      setShowWarning(false);
    } catch (submitError) {
      setError(submitError.message || 'Sign in failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showWarning && !mustReauthenticate ? (
        <div className="notice notice-info session-expiry-warning" role="status">
          <span>Your session will expire after 10 more minutes without activity.</span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              recordSessionActivity();
              setShowWarning(false);
            }}
          >
            Stay signed in
          </button>
        </div>
      ) : null}
      {mustReauthenticate ? (
        <div ref={dialogRef} className="report-issue-backdrop session-expiry-backdrop" role="dialog" aria-modal="true" aria-labelledby="session-expired-title">
          <div className="report-issue-sheet">
            <div className="report-issue-header">
              <div>
                <p className="eyebrow">Lesson paused</p>
                <h2 id="session-expired-title">Your session expired</h2>
              </div>
              <button
                type="button"
                className="btn btn-tertiary"
                aria-label="Close and go to sign-in page"
                onClick={goToSignInPage}
              >
                Close
              </button>
            </div>
            <div className="report-issue-body">
              <p>Sign in again with the same account. Your current page, selected answers, and marking work will stay open.</p>
              {error ? <p className="notice notice-error">{error}</p> : null}
              <form className="stack-md" onSubmit={handleSubmit}>
                <label htmlFor="session-expiry-email">Email address</label>
                <input id="session-expiry-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
                <label htmlFor="session-expiry-password">Password</label>
                <input ref={passwordRef} id="session-expiry-password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Signing in...' : 'Sign in and continue'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={goToSignInPage}>
                  Go to sign-in page
                </button>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default SessionExpiryGuard;
